import {
  Component,
  OnInit,
  ChangeDetectorRef,
  Input,
  OnDestroy,
} from '@angular/core';
import {
  UntypedFormGroup,
  Validators,
  UntypedFormBuilder,
  UntypedFormArray,
} from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil, tap } from 'rxjs/operators';
import Swal from 'sweetalert2';

import { FactoryService } from '@app/core/factories';
import { IRfqProductSubstitution } from '@app/core/inventory/store/inventory.model';
import { LotTrackingService } from '@app/core/lot-tracking/services/lot-tracking.service';
import { ProductTagService } from '@app/core/product-tags/services/product-tag.service';
import { CategoryService } from '@app/core/products/services/category.service';
import { RFQ, RfqService } from '@app/core/quotes';
import { SettingsService } from '@app/core/settings/services/settings.service';
import { ShippingCarrierService } from '@app/core/shipping-carriers/services/shipping-carrier.service';
import { UnitClassService } from '@app/core/unit-measurement/services/unit-class/unit-class.service';
import { UnitMeasureService } from '@app/core/unit-measurement/services/unit-measure/unit-measure.service';
import { environment } from '@app/env-service';
import { EFeatureFlag } from '@app/feature-toggle-list';
import {
  UnitClassInterface,
  UnitMeasureInterface,
} from '@app/modules/unit-measurement/interfaces/unit-measurement.interface';

@Component({
  selector: 'cnc-bulk-update-client-rfq-form',
  templateUrl: './bulk-update-client-rfq-form.component.html',
  styleUrls: ['./bulk-update-client-rfq-form.component.scss'],
})
export class BulkUpdateClientRfqFormComponent implements OnInit, OnDestroy {
  url :string = environment.url;

  pageTitle :string = 'Bulk Update SKU';
  searchFailed :boolean = false;
  searching :boolean = false;
  activeTab :number = 0;

  showFormError :boolean;
  submitted :boolean = false;
  loading :boolean = false;
  uploadedFilesCnt :number = 0;
  quoteForm :UntypedFormGroup;
  filesToUpload :any[] = [];
  fileRemarks :any[] = [];
  rfqAttachments :any[] = [];
  attachmentsToDelete :any[] = [];
  productVariants :any[] = [];
  rfq :any;
  factories :any[] = [];
  selectedCategory :any;

  tags :any = [];
  selectedTags :any = [];

  quote_expires :boolean = false;
  isBulkEdit :boolean = true;
  featureFlag = EFeatureFlag;

  @Input() ids :any;
  @Input() productType :any;

  baseUnitClass$ :Observable<UnitClassInterface>;
  baseUnitClass :UnitClassInterface;

  unitMeasures$ :Observable<UnitMeasureInterface[]>;
  unitMeasures :UnitMeasureInterface[] = [];

  unitClasses$ :Observable<UnitClassInterface[]>;
  unitClasses :UnitClassInterface[] = [];

  unitClassInput$ = new Subject<string>();
  unitMeasureInput$ = new Subject<string>();

  unitClassLoading$ :Observable<boolean>;
  unitMeasureLoading$ :Observable<boolean>;

  lotTrackingEnabled :boolean = false;

  productSubstitutions :IRfqProductSubstitution[] = [];
  isDeleteProductSubstitutions :boolean = false;

  private onDestroy$ = new Subject();

  constructor(
    public activeModal :NgbActiveModal,
    public productCategoriesSvc :CategoryService,
    public rfqService :RfqService,
    private fb :UntypedFormBuilder,
    private cd :ChangeDetectorRef,
    private toastr :ToastrService,
    public settingsSvc :SettingsService,
    public translate :TranslateService,
    public factoryService :FactoryService,
    public shippingCarrierSvc :ShippingCarrierService,
    public tagSvc :ProductTagService,
    private unitClassService :UnitClassService,
    private unitMeasureService :UnitMeasureService,
    private lotTrackingService :LotTrackingService,
  ) {}

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  ngOnInit() {
    this.checkLotTrackingIfEnabled();
    this.tagSvc.getTags({ parent_only: true }, null);
    this.tagSvc.formattedRecord$.subscribe((tags :any) => {
      this.tags = tags;
    });

    const dateNow = new Date();
    this.quoteForm = this.fb.group({
      product_dept_id: [null],
      is_palletized: [null],
      cartons_per_pallet: [null],
      shipment_value: [null],
      units_per_carton: [null],
      units_per_pallet: [null],

      //test
      pallet_length: [null],
      pallet_width: [null],
      pallet_height: [null],

      shipping_length: [null],
      shipping_width: [null],
      shipping_height: [null],

      product_length: [null],
      product_width: [null],
      product_height: [null],

      box_length: [null],
      box_width: [null],
      box_height: [null],
      // carton_weight: [null],
      carton_length: [null],
      carton_width: [null],
      carton_height: [null],
      // product_weight: [0],
      expires_at: [null],

      sub_category_id: [null],
      product_category_id: [null],
      product_description: [null],

      reorder_level: [null],
      qty_limit_notify: [null],
      oss_limit_notify: [null],

      // rfq_quote_type: [16, Validators.required],

      // product_has_variants: [""],
      shipping_weight: this.fb.array([]),
      product_weight: this.fb.array([]),
      dimensional_weight: this.fb.array([]),
      box_weight: this.fb.array([]),
      carton_weight: this.fb.array([]),
      pallet_weight: this.fb.array([]),

      material: this.fb.array([]),
      custom_fields: this.fb.array([]),
      product_links: this.fb.array([]),

      labor_cost: [null],
      package_cost: [null],
      default_shipping: [null],
      efficiency_score: [null],
      preferred_vendor_id: [null],

      internal_use_only: [null],
      photo: [null],
      product_design: [null],

      retail_price: [null],
      wholesale_price: [null],
      unit_cost: [null],

      // mirror_inventory_from_parent: [false],

      product_tags: [[]],
      unit_class_id: [null],
      default_selling_unit_id: [null],
      default_purchase_unit_id: [null],

      lot_tracking: [false],
      sellable: [true],
    });

    this.factoryService.factory$.subscribe((factories :any) => {
      this.factories = factories;
    });

    // this.addMaterials("material");
    // this.addLink("product_links");
    // this.addWeight("shippingWeights");
    // this.addWeight("dimensionalWeights");
    // this.addWeight("productWeights");
    // this.addWeight("boxWeights");
    // this.addWeight("cartonWeights");
    // this.addWeight("palletWeights");
    // this.addField();

    this.loadUnitMeasures();
    this.loadBaseUnitClass();
    this.loadUnitClasses();
    this.getDataFromService();
    this.getBaseUnitClass().subscribe();
    this.unitClassInputUpdated().subscribe();
    this.unitMeasureInputUpdated().subscribe();
  }

  trackUnitClassByFn(item :any) :number {
    return item.id;
  }

  trackUnitMeasureByFn(item :any) :number {
    return item.id;
  }

  private getBaseUnitClass() :Observable<UnitClassInterface> {
    return this.baseUnitClass$.pipe(
      tap((unitClass) => {
        this.setDefaultUnitClass(unitClass);
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private loadUnitMeasures() :void {
    this.unitMeasureService.get({
      limit: '100',
      unitClassId: this.quoteForm.value.unit_class_id,
    });
  }

  private setDefaultUnitClass(unitClass :any) :void {
    this.baseUnitClass = unitClass;
    this.loadUnitMeasures();
  }

  setUnitClass() :void {
    this.loadUnitMeasures();

    this.quoteForm.patchValue({
      default_selling_unit_id: null,
    });

    this.quoteForm.patchValue({
      default_purchase_unit_id: null,
    });
  }

  private unitMeasureInputUpdated() :Observable<string> {
    return this.unitMeasureInput$.asObservable().pipe(
      debounceTime(500), // 0.5 sec delay
      distinctUntilChanged(),
      tap((name :string) => {
        this.unitMeasureService.get({
          name: name,
          unitClassId: this.quoteForm.value.unit_class_id,
        });
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private unitClassInputUpdated() :Observable<string> {
    return this.unitClassInput$.asObservable().pipe(
      debounceTime(500), // 0.5 sec delay
      distinctUntilChanged(),
      tap((name :string) => {
        this.unitClassService.get({
          name: name,
        });
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private loadUnitClasses() :void {
    this.unitClassService.get({
      limit: '999',
    });
  }

  private getDataFromService() :void {
    this.baseUnitClass$ = this.unitClassService.baseUnitClass$;
    this.unitClasses$ = this.unitClassService.unitClasses$;
    this.unitMeasures$ = this.unitMeasureService.unitMeasures$;
    this.unitClassLoading$ = this.unitClassService.loading$;
    this.unitMeasureLoading$ = this.unitMeasureService.loading$;
  }

  private loadBaseUnitClass() :void {
    this.unitClassService.getBaseUnit();
  }

  getSubCategories() {
    this.selectedCategory = {};
    const category :any = this.productCategoriesSvc.getProductCategoryById(
      this.quoteForm.value.product_category_id,
    );
    if (!category || category.length == 0) {
      setTimeout(() => {
        this.getSubCategories();
      }, 500);
    }
    if (category && category.length > 0) {
      this.selectedCategory = category[0];
    }
  }

  addWeight(key :string, weight ?:any) {
    const weights = this.quoteForm.get(key) as UntypedFormArray;
    const weightFc :any = this.fb.group({
      wv: [weight ? weight.wv : 0, Validators.required],
      mu: [weight ? weight.mu : 'lbs', Validators.required],
    });
    weights.push(weightFc);
  }

  removeWeight(key :string, idx) {
    const weights = this.quoteForm.get(key) as UntypedFormArray;
    weights.removeAt(idx);
  }

  addField(field ?:any) {
    const fields = this.quoteForm.get('custom_fields') as UntypedFormArray;
    const fieldFc :any = this.fb.group({
      l: [field && field.l ? field.l : ''],
      v: [field && field.v ? field.v : ''],
    });
    fields.push(fieldFc);
  }

  removeField(idx) {
    const links = this.quoteForm.get('custom_fields') as UntypedFormArray;
    links.removeAt(idx);
  }

  addLink(key :string, link ?:any) {
    const links = this.quoteForm.get(key) as UntypedFormArray;
    const linkFc :any = this.fb.group({
      lv: [link ? link.lv : ''],
    });
    links.push(linkFc);
  }

  removeLink(key :string, idx) {
    const links = this.quoteForm.get(key) as UntypedFormArray;
    links.removeAt(idx);
  }

  addMaterials(key :string, mat ?:any) {
    const mats = this.quoteForm.get(key) as UntypedFormArray;
    const materialFc :any = this.fb.group({
      mv: [mat ? mat.mv : ' '],
    });
    mats.push(materialFc);
  }

  removeMaterials(key :string, idx) {
    const mats = this.quoteForm.get(key) as UntypedFormArray;
    mats.removeAt(idx);
  }

  submit() {
    const data :any = Object.assign({}, this.quoteForm.getRawValue());

    Object.keys(data).forEach((k) => {
      if (data[k] == null) delete data[k];
      if (Array.isArray(data[k]) && !data[k].length) delete data[k];
    });

    if (!Object.keys(data).length) {
      Swal.fire({
        title: this.translate.instant('MESSAGES.FORM_ERROR.TITLE'),
        text: 'Please update at least one field.',
        type: 'error',
        confirmButtonText: this.translate.instant('BUTTONS.OKAY'),
      });

      return;
    }

    if (!data.unit_class_id) {
      delete data.unit_class_id;
    }

    if (!data.default_purchase_unit_id) {
      delete data.default_purchase_unit_id;
    }

    if (!data.default_selling_unit_id) {
      delete data.default_selling_unit_id;
    }

    const productSubstitutions = [];

    this.productSubstitutions.forEach((item :IRfqProductSubstitution) => {
      const mappedItem = {
        id: item.id,
        priority: item.priority,
        substituteProductId: item.substituteProductId,
      };
      productSubstitutions.push(mappedItem);
    });

    data.productSubstitutions = JSON.stringify(productSubstitutions);
    data.isDeleteProductSubstitutions = this.isDeleteProductSubstitutions;

    this.submitted = true;
    this.loading = true;

    Swal.showLoading();

    this.rfqService.bulkUpdate({ ids: this.ids, data }).subscribe(
      (resp :any) => {
        // this.uploadFiles(resp.record, false);
        Swal.close();
        this.toastr.success('Update successful.');
        this.activeModal.close();
        this.loading = false;
      },
      (errorResp :any) => {
        Swal.fire({
          title: this.translate.instant('MESSAGES.SERVER_SAVE_ERROR.TITLE'),
          text: this.translate.instant('MESSAGES.SERVER_SAVE_ERROR.TEXT'),
          type: 'error',
          confirmButtonText: this.translate.instant('BUTTONS.OKAY'),
        }).then((data) => {
          Swal.close();
          this.loading = false;
          this.cd.markForCheck();
        });
      },
    );
  }

  // check if there are upload error and display the files not uploaded otherwise return created or updated
  showErroOrCloseForm(uploadErrors :any, rfq :any, isUpdate :boolean) {
    if (uploadErrors && uploadErrors.length > 0) {
      // message these rfq is saved but these files were not upload, please upload again.
      let filenames :any = '';
      uploadErrors.forEach((file) => {
        filenames = filenames + '<li>' + file.name + '</li>';
      });
      Swal.fire({
        title: 'File Upload Error',
        html:
          '<h6> These files were not uploaded. Please upload them again manually.' +
          '</h6>' +
          '<ul>' +
          filenames +
          '</ul>',
        type: 'warning',
        confirmButtonText: this.translate.instant('BUTTONS.OKAY'),
      }).then(() => {});
    } else {
      Swal.close();
      this.loading = false;
      if (isUpdate) {
        this.toastr.success('Successfully updated client SKU.', 'Success');
      } else {
        this.toastr.success('Successfully created client SKU.', 'Success');
      }
      this.activeModal.close(rfq);
    }
  }

  gotoNextTab(tabNumber :number) {
    this.submitted = true;
    this.activeTab = tabNumber;
    this.showFormError = false;
    this.submitted = false;
  }

  customTagsSearchFn(term :any, item :any) {
    term = term.toLocaleLowerCase();

    return item.name
      .toLowerCase()
      .replace(/&nbsp;/gi, '')
      .includes(term);
  }

  private checkLotTrackingIfEnabled() :void {
    this.lotTrackingService
      .checkLotTrackingEnabled()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((value :boolean) => {
        this.lotTrackingEnabled = value;
      });
  }

  setProductSubstitutionData(event :any) :void {
    this.productSubstitutions = event.currentProductSubstitutions;
    this.isDeleteProductSubstitutions = event.isDeleteProductSubstitutions;
  }
}
